import { useEffect, useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { RouterProvider } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Capacitor } from '@capacitor/core';
import { router } from './routes';
import { useAuthStore } from './stores/authStore';
import { useUIStore } from './stores/uiStore';
import AppShell from './components/AppShell';
// LandscapeGuard moved to AppLayout (needs useLocation)
import ForceUpdate from './components/ForceUpdate';
import ForceLogoutModal from './components/ForceLogoutModal';
import PinLock from './components/PinLock';
import './i18n';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  },
});

export default function App() {
  const { i18n } = useTranslation();
  const init = useAuthStore((s) => s.init);
  const loadFromStorage = useUIStore((s) => s.loadFromStorage);
  const user = useAuthStore((s) => s.user);
  const [pinLocked, setPinLocked] = useState(false);
  const pinUnlockedRef = useRef(false); // Track if user already unlocked this session
  const backgroundAtRef = useRef(0); // When app went to background (ms)
  const pinGracePeriod = (user?.pinGracePeriodSec ?? 10) * 1000;
  const lastBackPress = useRef(0);

  // Check if PIN should be shown
  const userPinEnabled = !!(user?.pinEnabled && user?.pinHash);
  const userPinHash = user?.pinHash;

  // Lock on initial load when user has PIN enabled
  const lockPin = useCallback(() => {
    if (userPinEnabled && userPinHash) {
      setPinLocked(true);
      pinUnlockedRef.current = false;
    }
  }, [userPinEnabled, userPinHash]);

  // Show PIN lock when user profile loads with PIN enabled (login / app start)
  useEffect(() => {
    if (userPinEnabled && !pinUnlockedRef.current) {
      setPinLocked(true);
    }
  }, [userPinEnabled]);

  // Background → foreground / tab visibility: re-lock PIN
  useEffect(() => {
    if (!userPinEnabled) return;

    // Web: tab visibility change
    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') {
        backgroundAtRef.current = Date.now();
      } else if (document.visibilityState === 'visible' && userPinEnabled) {
        const elapsed = Date.now() - backgroundAtRef.current;
        if (elapsed > pinGracePeriod) {
          setPinLocked(true);
          pinUnlockedRef.current = false;
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    // Native: app state change
    let removeListener: (() => void) | null = null;
    if (Capacitor.isNativePlatform()) {
      import('@capacitor/app').then(({ App: CapApp }) => {
        CapApp.addListener('appStateChange', ({ isActive }) => {
          if (!isActive) {
            backgroundAtRef.current = Date.now();
          } else if (userPinEnabled) {
            const elapsed = Date.now() - backgroundAtRef.current;
            if (elapsed > pinGracePeriod) {
              setPinLocked(true);
              pinUnlockedRef.current = false;
            }
          }
        }).then(handle => { removeListener = () => handle.remove(); });
      }).catch(() => {});
    }

    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      removeListener?.();
    };
  }, [userPinEnabled, pinGracePeriod]);

  // Android: double back press to exit
  useEffect(() => {
    if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'android') return;
    import('@capacitor/app').then(({ App }) => {
      App.addListener('backButton', ({ canGoBack }) => {
        if (canGoBack) {
          window.history.back();
        } else {
          const now = Date.now();
          if (now - lastBackPress.current < 2000) {
            App.exitApp();
          } else {
            lastBackPress.current = now;
            // Show toast-like message
            const toast = document.createElement('div');
            toast.textContent = i18n.language === 'ko' ? '한번 더 누르면 종료됩니다' : 'Press back again to exit';
            toast.style.cssText = 'position:fixed;bottom:80px;left:50%;transform:translateX(-50%);background:rgba(0,0,0,0.8);color:#fff;padding:8px 20px;border-radius:20px;font-size:13px;z-index:99999;pointer-events:none;';
            document.body.appendChild(toast);
            setTimeout(() => toast.remove(), 2000);
          }
        }
      });
    });
  }, []);

  // Dynamic document title based on language
  useEffect(() => {
    document.title = i18n.language === 'ko' ? '우리들의 주파수 (OurHertz)' : 'OurHertz';
  }, [i18n.language]);

  useEffect(() => {
    loadFromStorage();
    const cleanup = init();

    // Location permission is checked by AppLayout once authenticated. Avoid a second
    // check here — on native platforms the old probe would trigger the OS prompt.

    // iOS: Request App Tracking Transparency before any ad SDK initializes
    if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios') {
      import('./services/adService').then(({ initAdMob }) => initAdMob()).catch(() => {});
    }

    // Initialize native services after auth (per-user, re-runs on account switch)
    let lastInitUid = '';
    const initNativeServices = (uid: string) => {
      if (uid === lastInitUid) return; // Same user, skip
      lastInitUid = uid;
      import('./services/iapService').then(({ initIAP }) => initIAP(uid)).catch(() => {});
      import('./services/pushService').then(({ initPushNotifications, savePushToken }) =>
        initPushNotifications(uid, (token) => savePushToken(uid, token))
      ).catch(() => {});
    };

    // Check if user is already logged in
    const currentUser = useAuthStore.getState().user;
    if (currentUser?.uid) initNativeServices(currentUser.uid);

    // Also listen for future auth changes (login, account switch)
    const unsubAuth = useAuthStore.subscribe((state) => {
      if (state.user?.uid) {
        initNativeServices(state.user.uid);
      } else {
        lastInitUid = ''; // Reset on logout so next login re-inits
      }
    });

    return () => { cleanup(); unsubAuth(); };
  }, [init, loadFromStorage]);

  return (
    <QueryClientProvider client={queryClient}>
      <ForceUpdate />
      <ForceLogoutModal />
      <AppShell>
        <RouterProvider router={router} />
      </AppShell>
      {/* PIN overlay — app stays mounted underneath so uploads/state are preserved */}
      {pinLocked && userPinHash && (
        <PinLock pinHash={userPinHash} onUnlock={() => { setPinLocked(false); pinUnlockedRef.current = true; }} />
      )}
    </QueryClientProvider>
  );
}
